import { useState, useEffect } from "react";
import { API, axios } from "@/api";
import { Loader2, Users, Truck, MapPin, CreditCard, Gift, TrendingUp, Crown, Building2 } from "lucide-react";

export default function B2BDashboardPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/b2b-dashboard`)
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  if (!data) return <p className="p-4 text-sm text-slate-400">Failed to load B2B data</p>;

  const o = data.overview;
  const p = data.punch_stats;

  return (
    <div className="space-y-5 p-4" data-testid="b2b-dashboard-panel">
      {/* Overview Cards */}
      <div className="grid grid-cols-4 gap-2">
        <StatCard icon={<Users className="w-4 h-4 text-sky-500" />} label="Total Users" value={o.total_users} sub={`+${o.new_users_week} this week`} />
        <StatCard icon={<Truck className="w-4 h-4 text-amber-500" />} label="Vendors" value={o.total_vendors} sub={`${o.verified_vendors} verified`} />
        <StatCard icon={<Building2 className="w-4 h-4 text-emerald-500" />} label="Trucks" value={o.total_trucks} sub={`${o.live_now} live now`} />
        <StatCard icon={<TrendingUp className="w-4 h-4 text-violet-500" />} label="Pending" value={o.pending_vendors} sub="awaiting approval" />
      </div>

      {/* Punch Economy */}
      <section>
        <p className="text-xs font-bold text-slate-600 mb-2 flex items-center gap-1.5">
          <CreditCard className="w-3.5 h-3.5 text-amber-500" /> Punch Economy
        </p>
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-amber-50 rounded-xl p-3 border border-amber-100">
            <p className="text-xl font-black text-amber-600">{p.total_punches}</p>
            <p className="text-[9px] text-amber-500/70">Total Punches</p>
            <p className="text-[9px] text-amber-400 mt-0.5">{p.punches_this_month} this month</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100">
            <p className="text-xl font-black text-emerald-600">{p.total_redemptions}</p>
            <p className="text-[9px] text-emerald-500/70">Redemptions</p>
            <p className="text-[9px] text-emerald-400 mt-0.5">{p.redemptions_this_month} this month</p>
          </div>
          <div className="bg-violet-50 rounded-xl p-3 border border-violet-100">
            <p className="text-xl font-black text-violet-600">{p.conversion_rate}%</p>
            <p className="text-[9px] text-violet-500/70">Conversion Rate</p>
            <p className="text-[9px] text-violet-400 mt-0.5">punch → redeem</p>
          </div>
        </div>
      </section>

      {/* Multi-Truck Vendors */}
      {data.multi_truck_vendors.length > 0 && (
        <section>
          <p className="text-xs font-bold text-slate-600 mb-2 flex items-center gap-1.5">
            <Crown className="w-3.5 h-3.5 text-amber-500" /> Multi-Truck Vendors ({data.multi_truck_vendors.length})
          </p>
          <div className="bg-white rounded-xl border border-slate-100 divide-y divide-slate-50">
            {data.multi_truck_vendors.map((v) => (
              <div key={v.user_id} className="flex items-center justify-between px-3 py-2">
                <div>
                  <p className="text-xs font-semibold text-slate-700">{v.name}</p>
                  <p className="text-[9px] text-slate-400">{v.email}</p>
                </div>
                <span className="text-xs font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                  {v.truck_count} trucks
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Top Trucks */}
      {data.top_trucks.length > 0 && (
        <section>
          <p className="text-xs font-bold text-slate-600 mb-2 flex items-center gap-1.5">
            <Gift className="w-3.5 h-3.5 text-rose-500" /> Top Trucks by Followers
          </p>
          <div className="space-y-1.5">
            {data.top_trucks.map((t, i) => (
              <div key={t.business_id} className="flex items-center gap-2 bg-white rounded-lg p-2 border border-slate-100">
                <span className="text-[10px] font-black text-slate-300 w-5 text-center">#{i + 1}</span>
                {t.logo_url ? (
                  <img src={t.logo_url} alt="" className="w-7 h-7 rounded-lg object-cover" />
                ) : (
                  <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Truck className="w-3.5 h-3.5 text-slate-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-bold text-slate-700 truncate">{t.name}</p>
                  <p className="text-[9px] text-slate-400">{t.city}</p>
                </div>
                <span className="text-xs font-bold text-sky-600">{t.followers}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* City Breakdown */}
      {data.city_breakdown.length > 0 && (
        <section>
          <p className="text-xs font-bold text-slate-600 mb-2 flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5 text-teal-500" /> Trucks by City
          </p>
          <div className="grid grid-cols-3 gap-1.5">
            {data.city_breakdown.map((c) => (
              <div key={c.city} className="bg-white rounded-lg p-2 border border-slate-100 text-center">
                <p className="text-sm font-black text-slate-700">{c.trucks}</p>
                <p className="text-[9px] text-slate-400 truncate">{c.city}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, sub }) {
  return (
    <div className="bg-white rounded-xl p-3 border border-slate-100">
      <div className="flex items-center gap-1.5 mb-1">{icon}</div>
      <p className="text-xl font-black text-slate-800">{value ?? 0}</p>
      <p className="text-[9px] text-slate-500 font-semibold">{label}</p>
      {sub && <p className="text-[8px] text-slate-400">{sub}</p>}
    </div>
  );
}
